import mixpanel, { type RequestOptions } from "mixpanel-browser";

import type { VivChatSource } from "@intelligems/ig-types/schemas/visual-builder/variables";

import type { recommendationProperties } from "@intelligems/ig-analytics/recommendation-analytics";
import type { AuditRecommendationLocation } from "@intelligems/ig-types/functions/opportunity-audit/records/recommendations";
import { AiFeature } from "@intelligems/ig-types/schemas/ai/ai-chat-state";
import { BillingCadence } from "@intelligems/ig-types/dtos/billing/commands/catalog";
import {
  BlockTemplateType,
  BlockType,
} from "@intelligems/ig-types/dtos/checkout-blocks";
import { ComponentPlacementLocation } from "@intelligems/ig-types/dtos/components";
import {
  ExperienceDto,
  ExperienceTestTypeDto,
} from "@intelligems/ig-types/dtos/experiences";
import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { ExperienceListDto } from "@intelligems/ig-types/dtos/experiences-list";
import type { SlackConnectionMethod } from "@intelligems/ig-types/dtos/integrations";
import { LoginResponseBodyUserType } from "@intelligems/ig-types/functions/user-auths/login";
import { WidgetDto, WidgetType } from "@intelligems/ig-types/dtos/widgets";
import { camelToSnakeCase } from "@intelligems/ig-utils/common";
import type { SlackConnectAccount } from "@intelligems/ig-utils/slack-connect-account";
import {
  AnalyticsPageType,
  DashboardPageName,
  SitewideAnalyticsPageName,
} from "@src/api/types/dashboards";
import { IExperienceDashboardFilters } from "@src/store/types/dashboardFilters";
import { ISitewideDashboardFilters } from "@src/store/types/sitewideDashboardFilters";
import { shouldShowMobileExperience } from "@src/utils/shouldShowMobileExperience";
import {
  type BasePropertiesExperience,
  calculateModificationTypes,
  constructBaseMpPayload,
  constructBasePropertiesPayload,
  ModificationElementType,
} from "@src/utils/mpUtils";
import type { BillingAccountState } from "@src/components/BillingSettings2026/workflow";
import type { AccountUserState, OrderVolumeTier } from "@src/constants/billing";
import type { AccountAnalyticsProperties } from "@src/utils/accountAnalytics";
import type { StrategySessionDestination } from "@src/features/getStarted/GetStartedSupportResources.viewModel";

type BillingCheckoutStep =
  | "plan_selection"
  | "module_selection"
  | "payment"
  | "confirmation";

type SelfServeCommandsChangeType =
  | "new"
  | "upgrade"
  | "downgrade"
  | "add_module"
  | "remove_module"
  | "release_scheduled_cancellation"
  | "cancel_pending_module_removal"
  | "reactivation_from_pause";

export const mpLogin = (user: LoginResponseBodyUserType) => {
  mixpanel.identify(user.user.email);
  mixpanel.register({
    organization: user.organization.name,
    current_organization_id: user.user.organizationId,
    $name: `${user.user.firstName} ${user.user.lastName}`,
    $email: user.user.email,
    ai_enabled: user.organization.configurations.some(
      (config) =>
        config.key === "enable_ai" &&
        (config.value === true || config.value === "true")
    ),
  });
  track("login");
  mixpanel.people.set_once({
    first_login: new Date().toISOString(),
  });
  mixpanel.people.set({
    last_login: new Date().toISOString(),
    $name: `${user.user.firstName} ${user.user.lastName}`,
    $email: user.user.email,
    organization: user.organization.name,
  });
  mixpanel.people.increment("total_login");
};

export const mpLogout = () => {
  track("logout");
  mixpanel.reset();
};

export const mpClickAnalyticsButton = (
  experimentId: string,
  experimentName: string,
  entryPoint: string
) => {
  track("click_analytics_button", {
    experiment_id: experimentId,
    experiment_name: experimentName,
    entry_point: entryPoint,
  });
};

export const mpClickAnalyticsFilterSaveButton = (
  appliedFilters:
    | Partial<IExperienceDashboardFilters>
    | Partial<ISitewideDashboardFilters>,
  pageType: AnalyticsPageType,
  pageName?: DashboardPageName | SitewideAnalyticsPageName,
  productCategories?: string[],
  experienceType?: string,
  experienceId?: string
) => {
  track("analytic_filters_saved", {
    experience_id: experienceId,
    filters: appliedFilters,
    page_name: pageName,
    page_type: pageType,
    product_categories: productCategories,
    experience_type: experienceType,
  });
};

export const mpClickSubpageEntryPoint = (
  experienceId: string,
  entryPoint: string,
  subpage: string,
  metric?: string
) => {
  track("click_subpage_entry_point", {
    experience_id: experienceId,
    entry_point: entryPoint,
    subpage: subpage,
    metric: metric,
  });
};

export const mpAnalyticsMetricDetailClick = (
  experienceId: string,
  entryPoint: string,
  metric: string
) => {
  track("click_analytics_metric_detail", {
    experience_id: experienceId,
    entry_point: entryPoint,
    metric: metric,
  });
};

export const mpClickAddMetric = (
  experienceId?: string,
  entryPoint?: string,
  metric?: string,
  startTs?: number
) => {
  track("click_add_metric", {
    experience_id: experienceId,
    metric: metric,
    start_ts: startTs,
    entry_point: entryPoint,
  });
};

export const mpClickAnalyticsInteractiveLegend = (
  experienceId: string,
  variationId: string,
  entryPoint: string
) => {
  track("analytics_interactive_legend_clicked", {
    experience_id: experienceId,
    variation_id: variationId,
    entry_point: entryPoint,
  });
};

export const mpClickClassicOnsiteEditorButton = () => {
  track("onsite_editor_launched");
};

export const mpViewPage = (pageName: string) => {
  track("view_page", { page_name: pageName });
};

export const mpClickAiExperienceAnalysisButton = () => {
  track("click_ai_experiment_analysis_button");
};

export const mpClickAiExperienceCreationButton = () => {
  track("ai_experiment_creation_chat_opened");
};

export const mpClickAiExperienceAnalysisSummaryButton = () => {
  track("click_ai_experiment_analysis_summary_button");
};

export const mpClickAiExperienceAnalysisCustomButton = () => {
  track("click_ai_experiment_analysis_custom_button");
};

export const mpClickAiExperienceAskAbieButton = (query: string) => {
  track("click_ai_experiment_ask_abie_button", { query });
};

export const mpClickAiExperienceRegenerateButton = () => {
  track("click_ai_experiment_regenerate_button");
};

export const mpRateAi = (
  feature: AiFeature,
  rating: string,
  sessionId: string,
  comments?: string,
  metadata?: Record<string, string>
) => {
  const eventData: Record<string, string> = {
    ...metadata,
    rating,
    sessionId,
    chat_source: feature,
  };

  if (comments) {
    eventData.comments = comments;
  }

  track(`ai_chat_rated`, eventData);
};

export const mpClickDocLink = (
  docLink: string,
  source?: string,
  embedded = false,
  docLinkUrl?: string
) => {
  track("doc_link_clicked", {
    doc_link: docLink,
    source,
    is_embedded: embedded,
    doc_link_url: docLinkUrl,
  });
};

export const mpClickVideoLink = (
  videoLink: string,
  source?: string,
  embedded = false
) => {
  track("video_link_clicked", {
    video_link: videoLink,
    source,
    is_embedded: embedded,
  });
};

// `source` = which surface fired the event: the widget library or the offer builder.
export const mpClickSaveWidgetButton = (
  performingUserId: string | null,
  widget: WidgetDto,
  source: "widget_library" | "offer_builder"
) => {
  track("component_saved", {
    widget_id: widget.id,
    widget_type: widget.widgetType,
    performing_user: performingUserId,
    source,
  });
};

// Fires `component_created` from both the widget-library listener and the offer builder.
export const mpComponentCreated = (
  widgetType: WidgetType,
  source: "widget_library" | "offer_builder"
) => {
  track("component_created", {
    widget_type: widgetType,
    source,
  });
};

// Fires `component_edit_started` from both the widget-library listener and the offer builder.
export const mpComponentEditStarted = (
  widgetType: WidgetType,
  source: "widget_library" | "offer_builder"
) => {
  track("component_edit_started", {
    widget_type: widgetType,
    source,
  });
};

export const mpClickEnqueueTestCategorization = (experienceId: string) => {
  track("click_enqueue_test_categorization", {
    experiment_id: experienceId,
  });
};

export const mpClickApproveTestCategorization = (experienceId: string) => {
  track("click_approve_test_categorization", {
    experiment_id: experienceId,
  });
};

export const mpClickSavedTestCategorization = (experienceId: string) => {
  track("click_save_test_categorization", {
    experiment_id: experienceId,
  });
};

export const mpOnboardingStepComplete = (
  stepName: string,
  currentStep: number,
  data: { [key: string]: string | boolean | null | undefined | string[] },
  earlyExitReason?: string
) => {
  track("onboarding_step_complete", {
    screen_name: stepName,
    screen_order: currentStep + 1, // add 1 bc of index
    data,
    ...(earlyExitReason && {
      early_exit_reason: earlyExitReason,
    }),
  });
};

export const mpScriptIntegration = (
  screenName: string,
  buttonClicked: string,
  // Only the buttons that advance the flow report a script status; the rest
  // omit it so MixPanel's "is set" filters isolate the funnel step.
  scriptStatus?: "verified" | "unverified"
) => {
  track("script_integration", {
    button_clicked: buttonClicked,
    screen_name: screenName,
    script_status: scriptStatus,
  });
};

export const mpClickOffersMarketingBanner = () => {
  track("click_offers_announcement_banner");
};

export const mpExperienceGenericAction = function (
  experience: Pick<
    ExperienceDto | ExperienceListDto | ExperienceFe,
    | "testTypes"
    | "id"
    | "name"
    | "category"
    | "variations"
    | "productVariantCount"
  >,
  action: "saved" | "reset" | "deleted" | "viewed",
  source?: string
) {
  const properties: Record<string, number | string | string[] | undefined> =
    constructBaseMpPayload(experience, collectOffers(experience));
  track(`experience_${action}`, { ...properties, source });
};

export const mpExperienceEditStarted = function (
  experience: Pick<
    ExperienceDto | ExperienceListDto | ExperienceFe,
    | "testTypes"
    | "id"
    | "name"
    | "category"
    | "variations"
    | "productVariantCount"
  >
) {
  const properties: Record<string, number | string | string[] | undefined> =
    constructBaseMpPayload(experience, collectOffers(experience));
  track(`experience_edit_started`, properties);
};

export const mpTrackCmdK = (props: {
  action: "opened" | "closed" | "selected";
}) => {
  track("cmdk_interacted", props);
};

export const mpOnboardingChecklistItemComplete = (
  stepNumber: number,
  stepName: string
) => {
  track("onboarding_checklist_item_complete", {
    step_number: stepNumber,
    step_name: stepName,
  });
};

export const mpOfferCreationStarted = (offerType: string) => {
  track("offer_creation_started", {
    offer_type: offerType,
  });
};

export const mpOfferCreationCompleted = (
  hasComponent: boolean,
  offerType: string
) => {
  track("offer_creation_completed", {
    has_component: hasComponent,
    offer_type: offerType,
  });
};

export const mpExperienceCreated = (
  experienceType: "personalization" | "test",
  source: string,
  productCategories: string[],
  createdFromDuplicate?: boolean,
  duplicatedFromExperienceId?: string,
  /** The recommendation it was built from. */
  ideaId?: string
) => {
  track("experience_created", {
    experience_type: experienceType,
    source: source,
    product_categories: productCategories,
    created_from_duplicate: createdFromDuplicate ?? false,
    duplicated_from_experience_id: duplicatedFromExperienceId,
    idea_id: ideaId,
  });
};

export const mpOfferEdited = (
  offerId: string,
  experienceType: string,
  experienceId: string | null | undefined,
  offerType: string
) => {
  track("offer_edited", {
    offer_id: offerId,
    experience_type: experienceType,
    experience_id: experienceId,
    offer_type: offerType,
  });
};

export const mpCheckoutBlockEdited = (
  checkoutBlockId: string,
  experienceType: string,
  experienceId: string | null | undefined
) => {
  track("checkout_edited", {
    checkout_block_id: checkoutBlockId,
    experience_type: experienceType,
    experience_id: experienceId,
  });
};

export const mpPostPurchaseEdited = (
  postPurchaseBlockId: string,
  experienceType: string,
  experienceId: string | null | undefined
) => {
  track("post_purchase_edited", {
    post_purchase_block_id: postPurchaseBlockId,
    experience_type: experienceType,
    experience_id: experienceId,
  });
};

export const mpCheckoutBlockCreationCompleted = (blockType: BlockType) => {
  track("checkout_creation_completed", {
    checkout_type: camelToSnakeCase(blockType),
  });
};

export const mpPostPurchaseCreationCompleted = () => {
  track("post_purchase_creation_completed");
};

export const mpCheckoutBlockTemplateChosen = (
  blockTemplate: BlockTemplateType | null | undefined
) => {
  track("checkout_block_template_chosen", {
    checkout_block_template: blockTemplate
      ? camelToSnakeCase(blockTemplate)
      : undefined,
  });
};

export const mpCheckoutBlockCreationStarted = (blockType?: BlockType) => {
  track("checkout_creation_started", {
    checkout_type: blockType ? camelToSnakeCase(blockType) : undefined,
  });
};

export const mpPostPurchaseCreationStarted = () => {
  track("post_purchase_creation_started");
};

type OnsiteUpsellType = "simple" | "bundle";

export const mpOnsiteUpsellCreationStarted = (props: {
  upsellType: OnsiteUpsellType;
  // Empty until the first save materializes the experience / component.
  experienceId?: string;
  componentId?: string;
}) => {
  track("onsite_upsell_creation_started", {
    experience_id: props.experienceId,
    component_id: props.componentId,
    upsell_type: props.upsellType,
  });
};

export const mpOnsiteUpsellCreationCompleted = (props: {
  componentId: string;
  upsellType: OnsiteUpsellType;
  hasDiscount: boolean;
  surface: ComponentPlacementLocation;
  experienceId?: string;
}) => {
  track("onsite_upsell_creation_completed", {
    experience_id: props.experienceId,
    component_id: props.componentId,
    upsell_type: props.upsellType,
    has_discount: props.hasDiscount,
    surface: props.surface,
  });
};

export const mpOnsiteUpsellEdited = (props: {
  componentId: string;
  upsellType: OnsiteUpsellType;
  experienceType: string;
  surface: ComponentPlacementLocation;
  experienceId?: string;
}) => {
  track("onsite_upsell_edited", {
    component_id: props.componentId,
    experience_id: props.experienceId,
    experience_type: props.experienceType,
    surface: props.surface,
    upsell_type: props.upsellType,
  });
};

/**
 * @deprecated Superseded by `mpSlackConnectStarted`. Despite the name, this
 * fires on the connect/reconnect click, not on an actual connection, so it
 * counts intent rather than outcome. Kept firing at its original call sites so
 * existing dashboards keep a continuous series. Remove once nothing reports on
 * `slack_support_channel_connected`.
 */
export const mpSlackIntegrationEnabled = () => {
  track("slack_support_channel_connected");
};

/**
 * Every place a customer can start a Slack connection. Kept exhaustive rather
 * than defaulted, because a surface that forgets to declare itself shows up as
 * missing starts rather than as an obvious gap.
 */
export type SlackConnectSurface =
  | "integrations_card"
  | "settings_page"
  | "settings_alerts"
  | "get_help"
  | "get_started";

const slackConnectProperties = (
  surface: SlackConnectSurface,
  connectionMethod: SlackConnectionMethod,
  account: SlackConnectAccount
) => ({
  connection_method: connectionMethod,
  surface,
  ...account,
});

/**
 * A connect prompt was shown. `connection_method` is the path a click would
 * start: member connect when it's offered, since the choice modal opens on it,
 * and admin OAuth otherwise.
 */
export const mpSlackConnectViewed = (
  surface: SlackConnectSurface,
  {
    memberConnectAvailable,
    account,
  }: { memberConnectAvailable: boolean; account: SlackConnectAccount }
) => {
  track("slack_connect_viewed", {
    ...slackConnectProperties(
      surface,
      memberConnectAvailable ? "member_connect" : "admin_oauth",
      account
    ),
    member_connect_available: memberConnectAvailable,
  });
};

/**
 * Failures that happen before we hand the customer to Slack. Nothing on the
 * server can report these, because no callback ever arrives, so without them
 * they show up as an unexplained gap between started and nothing.
 */
export type SlackConnectFailureReason =
  | "oauth_url_request_failed"
  | "oauth_url_missing"
  | "popup_blocked";

export const mpSlackConnectFailed = (
  reason: SlackConnectFailureReason,
  surface: SlackConnectSurface,
  account: SlackConnectAccount
) => {
  track("slack_connect_failed", {
    ...slackConnectProperties(surface, "admin_oauth", account),
    failure_stage: "pre_redirect",
    failure_reason: reason,
  });
};

// The checkpoints only the browser sees, and the path each one belongs to.
// The backend logs the rest.
const SLACK_CONNECT_BROWSER_STEPS = {
  // We're sending the customer to Slack's permission screen.
  oauth_redirect_initiated: "admin_oauth",
  // An admin picked a channel for alerts.
  channel_selected: "admin_oauth",
  invite_resend_clicked: "member_connect",
} as const satisfies Record<string, SlackConnectionMethod>;

export const mpSlackConnectStepCompleted = (
  step: keyof typeof SLACK_CONNECT_BROWSER_STEPS,
  surface: SlackConnectSurface,
  account: SlackConnectAccount
) => {
  track("slack_connect_step_completed", {
    ...slackConnectProperties(
      surface,
      SLACK_CONNECT_BROWSER_STEPS[step],
      account
    ),
    step,
  });
};

export const mpSlackConnectStarted = (
  surface: SlackConnectSurface,
  connectionMethod: SlackConnectionMethod,
  account: SlackConnectAccount
) => {
  track(
    "slack_connect_started",
    slackConnectProperties(surface, connectionMethod, account)
  );
};

export const mpCheckoutBlockRequestAccessClick = () => {
  track("checkout_access_request_clicked");
};

export const mpPostPurchaseRequestAccessClick = () => {
  track("post_purchase_access_request_clicked");
};

export const mpPostPurchaseFunnelsRequestAccessClick = () => {
  track("post_purchase_funnels_access_request_clicked");
};

export const mpLikelihoodPurchaseRequestAccessClick = () => {
  track("likelihood_to_purchase_access_request_clicked");
};

interface ExperienceInfo {
  id?: string;
  testTypes: ExperienceTestTypeDto;
}

export const mpMobileAnalyticsViewed = (info?: ExperienceInfo) => {
  track("analytics_viewed", mobileAnalyticsPayloadWithEvent(info));
};

export const mpMobileFilterSaved = (info?: ExperienceInfo) => {
  track("analytics_filter_saved", mobileAnalyticsPayloadWithEvent(info));
};

export const mpMobileDeviceModeSwitched = (
  deviceMode: "mobile" | "desktop",
  info?: ExperienceInfo
) => {
  track("device_mode_switched", {
    device_mode: deviceMode,
    ...mobileAnalyticsPayloadWithEvent(info),
  });
};

export const mpBenchmarkClickExploreMore = () => {
  track("click_benchmark_tracker_explore_more");
};

export const mpBenchmarkUpdateFilter = (
  filter: string,
  value: string,
  page?: string
) => {
  track("benchmark_filters_saved", { filter, value, page });
};

export const mpSideNavClick = (itemName: string, itemPath: string) => {
  track("drawer_navigation_click", {
    item_name: itemName,
    item_path: itemPath,
  });
};

export const mpManageSubscriptionDialog = (
  page: string,
  event: string,
  additionalData?: Record<string, unknown>
) => {
  track("manage_subscription_dialog_viewed", {
    page,
    event,
    ...additionalData,
  });
};

// send_immediately prevents duplicate events when window.open triggers blur events
// that cause Mixpanel to flush its batch queue twice
export const mpBillingNudgeClick = (
  nudgeType: string,
  buttonAction: string,
  additionalData?: Record<string, unknown>
) => {
  trackTrialFunnel(
    "billing_nudge_click",
    {
      nudge_type: nudgeType,
      button_action: buttonAction,
      ...additionalData,
    },
    { send_immediately: true }
  );
};

export const mpProductCardClick = (
  planName: string,
  buttonText: string | null,
  planId: string,
  additionalData?: Record<string, unknown>
) => {
  track(
    "billing_plan_change_started",
    {
      plan_name: planName,
      button_text: buttonText,
      plan_id: planId,
      ...additionalData,
    },
    { send_immediately: true }
  );
};

export const mpPlanComparisonClick = () => {
  track("plan_comparison_click", {}, { send_immediately: true });
};

export const mpBookDemoClick = (source?: string) => {
  track("book_demo_click", { source }, { send_immediately: true });
};

export const mpSalesNudgeViewed = (source: string) => {
  track("sales_nudge_viewed", { source });
};

export const mpPriceTestIntegrationBannerClick = (
  helpLink: string,
  helpLinkText: string
) => {
  track(
    "price_test_integration_banner_click",
    {
      help_link: helpLink,
      help_link_text: helpLinkText,
    },
    { send_immediately: true }
  );
};

export const mpShareLinkCreated = (experienceId: string) => {
  track("share_link_created", {
    experience_id: experienceId,
  });
};

export const mpShareLinkDeleted = (experienceId: string) => {
  track("share_link_deleted", {
    experience_id: experienceId,
  });
};

export const mpShareLinkViewed = (
  experienceId: string,
  context?: { referrer?: string; userAgent?: string }
) => {
  track("share_link_viewed", {
    experience_id: experienceId,
    referrer: context?.referrer ?? getDocumentReferrer(),
    device_type: getDeviceType(context?.userAgent),
  });
};

export const mpIgUncutOpened = () => {
  track("ig_uncut_opened");
};

export const mpIgUncutPageViewed = (pageNumber: number) => {
  track("ig_uncut_page_viewed", {
    page_number: pageNumber,
  });
};

export const mpIgUncutSharedCompleted = (pageNumber: number) => {
  track("ig_uncut_share_completed", {
    page_number: pageNumber,
  });
};

export const mpIgUncutSharedStarted = (pageNumber: number) => {
  track("ig_uncut_share_started", {
    page_number: pageNumber,
  });
};

export const mpIgUncutClosed = (source: string) => {
  track("ig_uncut_closed", {
    source,
  });
};

export const mpClickGetHelp = (source?: string) => {
  track("get_help_clicked", { source });
};

export const mpSupportTicketSubmitted = (
  testType: string,
  refPath?: string | null
) => {
  track("support_ticket_submitted", {
    test_type: testType,
    ...(refPath ? { ref_path: refPath } : {}),
  });
};

export const mpSupportTicketSubmissionFailed = (testType: string) => {
  track("support_ticket_submission_failed", { test_type: testType });
};

export const mpSlackChannelOpened = (source: string) => {
  track("slack_channel_opened", { source });
};

export const mpInternalPreviewFeedback = (
  orgId: string,
  shopId: string,
  orgName: string,
  feedback: "works" | "broken" | "invalid_domain",
  notes: string,
  userEmail: string
) => {
  track("temp_internal_preview_feedback", {
    current_organization_id: orgId,
    shop_id: shopId,
    organization_name: orgName,
    feedback,
    eng_notes: notes,
    distinct_id: userEmail,
  });
};

// Pylon Ticket Analysis Events
export const mpPylonAnalysisViewed = (props: {
  issueId: string;
  ticketTitle: string;
  customerName: string;
  hasAnalysis: boolean;
  hasDocs: boolean;
  hasCode: boolean;
  hasFlags: boolean;
  conversationCount: number;
  priority?: string;
  questionType?: string;
}) => {
  track("pylon_analysis_viewed", {
    issue_id: props.issueId,
    ticket_title: props.ticketTitle,
    customer_name: props.customerName,
    has_analysis: props.hasAnalysis,
    has_docs: props.hasDocs,
    has_code: props.hasCode,
    has_flags: props.hasFlags,
    conversation_count: props.conversationCount,
    priority: props.priority,
    question_type: props.questionType,
  });
};

export const mpPylonOpenInPylonClicked = (issueId: string) => {
  track("pylon_open_in_pylon_clicked", { issue_id: issueId });
};

export const mpPylonDocLinkClicked = (props: {
  issueId: string;
  docTitle: string;
  docUrl: string;
}) => {
  track("pylon_doc_link_clicked", {
    issue_id: props.issueId,
    doc_title: props.docTitle,
    doc_url: props.docUrl,
  });
};

export const mpPylonAccordionToggled = (props: {
  issueId: string;
  section: string;
  expanded: boolean;
}) => {
  track("pylon_accordion_toggled", {
    issue_id: props.issueId,
    section: props.section,
    expanded: props.expanded,
  });
};

export const mpShiftAnalysisViewed = (props: {
  linearIssueId: string;
  ticketTitle: string;
  orgName: string;
  hasAnalysis: boolean;
  hasDocs: boolean;
  hasCode: boolean;
  hasFlags: boolean;
  severity?: string;
  category?: string;
}) => {
  track("shift_analysis_viewed", {
    linear_issue_id: props.linearIssueId,
    ticket_title: props.ticketTitle,
    org_name: props.orgName,
    has_analysis: props.hasAnalysis,
    has_docs: props.hasDocs,
    has_code: props.hasCode,
    has_flags: props.hasFlags,
    severity: props.severity,
    category: props.category,
  });
};

export const mpShiftDocLinkClicked = (props: {
  issueId: string;
  docTitle: string;
  docUrl: string;
}) => {
  track("shift_doc_link_clicked", {
    issue_id: props.issueId,
    doc_title: props.docTitle,
    doc_url: props.docUrl,
  });
};

export const mpVisualBuilderRequestAccessClicked = () => {
  track("visual_builder_request_access_clicked");
};

export const mpPostPurchaseFunnelRequestAccessClicked = () => {
  track("post_purchase_funnel_request_access_clicked");
};

// Visual Builder Events
export interface VisualBuilderBasePayload {
  experience_id?: string;
  experience_name?: string;
  experience_type: string;
  test_group_count: number;
  modification_count: number;
  modification_types: string[];
  ai_modification_count: number;
  ai_modification_types: string[];
  component_count: number;
  components: string[];
  product_categories: string[];
  mod_count?: number;
  builder_mode?: "mobile" | "desktop" | "any";
}

export const mpVisualBuilderOpened = (payload: VisualBuilderBasePayload) => {
  track("visual_builder_opened", { ...payload });
};

export const mpVisualBuilderCloseStarted = (
  payload: VisualBuilderBasePayload
) => {
  track("visual_builder_close_started", { ...payload });
};

export const mpVisualBuilderCloseCompleted = (
  payload: VisualBuilderBasePayload & {
    close_modal_choice?: "save" | "discard";
  }
) => {
  track("visual_builder_close_completed", { ...payload });
};

export const mpVisualBuilderAllModsSaved = (
  payload: VisualBuilderBasePayload
) => {
  track("visual_builder_all_mods_saved", { ...payload });
};

export const mpVisualBuilderElementSelected = (props: {
  experienceId?: string;
  experienceName?: string;
  experienceType: string;
  selector: string;
  elementType: ModificationElementType;
  originalText?: string;
  groupName: string;
  builderMode?: "mobile" | "desktop" | "any";
}) => {
  track("visual_builder_element_selected", {
    experience_id: props.experienceId,
    experience_name: props.experienceName,
    experience_type: props.experienceType,
    selector: props.selector,
    element_type: props.elementType,
    original_text: props.originalText,
    group_name: props.groupName,
    builder_mode: props.builderMode,
  });
};

export const mpVisualBuilderModSaved = (props: {
  experienceId?: string;
  experienceName?: string;
  experienceType: string;
  selector: string;
  elementType: ModificationElementType;
  originalText?: string;
  elementDescription?: string;
  editType?: string;
  imageReplaceAll?: boolean;
  groupName: string;
  builderMode?: "mobile" | "desktop" | "any";
  createdFromDuplicate?: boolean;
  duplicatedFromGroupName?: string;
  duplicatedFromGroupId?: string;
  isAiGenerated: boolean;
}) => {
  track("visual_builder_mod_saved", {
    experience_id: props.experienceId,
    experience_name: props.experienceName,
    experience_type: props.experienceType,
    selector: props.selector,
    element_type: props.elementType,
    original_text: props.originalText,
    element_description: props.elementDescription,
    edit_type: props.editType,
    image_replace_all: props.imageReplaceAll,
    group_name: props.groupName,
    builder_mode: props.builderMode,
    is_ai_generated: props.isAiGenerated,
    ...(props.createdFromDuplicate && {
      created_from_duplicate: props.createdFromDuplicate,
      duplicated_from_group_name: props.duplicatedFromGroupName,
      duplicated_from_group_id: props.duplicatedFromGroupId,
    }),
  });
};

export const mpVisualBuilderModEditStarted = (props: {
  experienceId?: string;
  experienceName?: string;
  experienceType: string;
  selector: string;
  elementType: ModificationElementType;
  originalText?: string;
  elementDescription?: string;
  editType?: string;
  imageReplaceAll?: boolean;
  groupName: string;
  builderMode?: "mobile" | "desktop" | "any";
}) => {
  track("visual_builder_mod_edit_started", {
    experience_id: props.experienceId,
    experience_name: props.experienceName,
    experience_type: props.experienceType,
    selector: props.selector,
    element_type: props.elementType,
    original_text: props.originalText,
    element_description: props.elementDescription,
    edit_type: props.editType,
    image_replace_all: props.imageReplaceAll,
    group_name: props.groupName,
    builder_mode: props.builderMode,
  });
};

export const mpVisualBuilderModEditCancelled = (props: {
  experienceId?: string;
  experienceName?: string;
  experienceType: string;
  selector: string;
  elementType: ModificationElementType;
  originalText?: string;
  elementDescription?: string;
  editType?: string;
  imageReplaceAll?: boolean;
  groupName: string;
  hasUnsavedChanges: boolean;
  builderMode?: "mobile" | "desktop" | "any";
}) => {
  track("visual_builder_mod_edit_cancelled", {
    experience_id: props.experienceId,
    experience_name: props.experienceName,
    experience_type: props.experienceType,
    selector: props.selector,
    element_type: props.elementType,
    original_text: props.originalText,
    element_description: props.elementDescription,
    edit_type: props.editType,
    image_replace_all: props.imageReplaceAll,
    group_name: props.groupName,
    has_unsaved_changes: props.hasUnsavedChanges,
    builder_mode: props.builderMode,
  });
};

export const mpVisualBuilderModDeleted = (props: {
  experienceId?: string;
  experienceName?: string;
  experienceType: string;
  selector: string;
  elementType: ModificationElementType;
  originalText?: string;
  elementDescription?: string;
  editType?: string;
  imageReplaceAll?: boolean;
  groupName: string;
  builderMode?: "mobile" | "desktop" | "any";
}) => {
  track("visual_builder_mod_deleted", {
    experience_id: props.experienceId,
    experience_name: props.experienceName,
    experience_type: props.experienceType,
    selector: props.selector,
    element_type: props.elementType,
    original_text: props.originalText,
    element_description: props.elementDescription,
    edit_type: props.editType,
    image_replace_all: props.imageReplaceAll,
    group_name: props.groupName,
    builder_mode: props.builderMode,
  });
};

/**
 * Fires when a merchant hides or shows mods in their own visual editor
 * preview. Hiding is view-only and session-scoped — it never changes what
 * shoppers receive — so this measures comparison behaviour, not editing.
 *
 * - `visibility`: the resulting state of the mods acted on.
 * - `hidden_mod_count`: how many mods in the previewed group are hidden after
 *   the action.
 */
export const mpVisualBuilderModVisibilityToggled = (
  payload: VisualBuilderBasePayload & {
    visibility: "hidden" | "shown";
    hidden_mod_count: number;
  }
) => {
  track("visual_builder_mod_visibility_toggled", { ...payload });
};

export const mpVisualBuilderDiscardModalViewed = (
  payload: VisualBuilderBasePayload & {
    source: "modification_edit" | "close_builder";
  }
) => {
  track("visual_builder_discard_modal_viewed", { ...payload });
};

export const mpVisualBuilderDiscardModalClosed = (
  payload: VisualBuilderBasePayload & {
    choice:
      | "keep_editing"
      | "discard_and_exit"
      | "save_and_exit"
      | "apply_changes";
  }
) => {
  track("visual_builder_discard_modal_closed", { ...payload });
};

export const mpVisualBuilderGroupToggled = (props: {
  experienceId?: string;
  experienceName?: string;
  experienceType: string;
  groupName: string;
  groupId: string;
  modCount: number;
  builderMode?: "mobile" | "desktop" | "any";
}) => {
  track("visual_builder_group_toggled", {
    experience_id: props.experienceId,
    experience_name: props.experienceName,
    experience_type: props.experienceType,
    group_name: props.groupName,
    group_id: props.groupId,
    mod_count: props.modCount,
    builder_mode: props.builderMode,
  });
};

export const mpVisualBuilderControlGroupUnlocked = (props: {
  experienceId?: string;
  experienceName?: string;
  experienceType: string;
  testGroupCount: number;
  builderMode?: "mobile" | "desktop" | "any";
}) => {
  track("visual_builder_control_group_unlocked", {
    experience_id: props.experienceId,
    experience_name: props.experienceName,
    experience_type: props.experienceType,
    test_group_count: props.testGroupCount,
    builder_mode: props.builderMode,
  });
};

export const mpVisualBuilderModeToggled = (props: {
  experienceId?: string;
  experienceName?: string;
  experienceType: string;
  groupName: string;
  modCount: number;
  toggledTo: "select" | "navigate" | "desktop" | "mobile";
}) => {
  track("visual_builder_mode_toggled", {
    experience_id: props.experienceId,
    experience_name: props.experienceName,
    experience_type: props.experienceType,
    group_name: props.groupName,
    mod_count: props.modCount,
    toggled_to: props.toggledTo,
  });
};

export const mpVivChatToggled = (props: {
  experienceId?: string;
  chatId?: string;
  /** What opened the session, and the recommendation it builds. */
  origin?: { source: VivChatSource; recommendationId: string };
  opened: boolean;
}) => {
  track("chat_toggled", {
    agent_name: "viv",
    chat_source: props.origin?.source,
    idea_id: props.origin?.recommendationId,
    experience_id: props.experienceId,
    chat_id: props.chatId,
    opened: props.opened,
  });
};

/**
 * Where an Ellie session was started from:
 * - `homepage`: the AskAtlas "build an experience" box (`AskAtlasBuildExperience`)
 * - `sparkle`: the sparkle button in the experience page's top bar
 * - `viv_handoff`: Viv handing the conversation over mid-chat
 *
 * Fixed for the life of the session — reopening the panel via the sparkle
 * doesn't overwrite the source a session actually started from. Also sent to
 * the server as `variables.chatSource` so its `chat_*` events carry it.
 */
export type EllieChatSource = "homepage" | "sparkle" | "viv_handoff";

/**
 * Experience-creation agent ("Ellie") chat panel opened or closed. Server-side
 * `chat_*` events carry the same `experience_id`/`chat_id`/`chat_source`, so a
 * funnel can join panel opens to the turns that followed.
 */
export const mpEllieChatToggled = (props: {
  experienceId?: string;
  chatId?: string;
  chatSource?: EllieChatSource;
  opened: boolean;
}) => {
  track("chat_toggled", {
    agent_name: "ellie",
    experience_id: props.experienceId,
    chat_id: props.chatId,
    chat_source: props.chatSource,
    opened: props.opened,
  });
};

export const mpVivIdeaLibraryViewed = (props: {
  experienceId?: string;
  chatId?: string;
}) => {
  track("viv_idea_library_viewed", {
    experience_id: props.experienceId,
    chat_id: props.chatId,
  });
};

export const mpVivLibraryIdeaChosen = (props: {
  experienceId?: string;
  chatId?: string;
  ideaId: string;
  /** Full prompt text sent to Viv (not the short list label). */
  idea: string;
  /** Chosen from the empty state's featured chips rather than the library. */
  isDefault: boolean;
}) => {
  track("viv_library_idea_chosen", {
    experience_id: props.experienceId,
    chat_id: props.chatId,
    idea_id: props.ideaId,
    idea: props.idea,
    is_default: props.isDefault,
  });
};

export type VisualBuilderRevertFeedbackReason =
  | "missing_feature"
  | "bug"
  | "prefer_old_ui"
  | "other";

export const mpVisualBuilderRevertFeedbackSubmitted = (props: {
  reason: VisualBuilderRevertFeedbackReason;
  comments: string;
}) => {
  track(
    "visual_builder_revert_feedback_submitted",
    { reason: props.reason, comments: props.comments },
    { send_immediately: true }
  );
};

export const mpUpsellProductTargetingRequestAccessClicked = () => {
  track("upsell_rule_builder_request_access_clicked");
};

export const mpRequestBlockTypeClicked = () => {
  track("checkout_request_block_type_clicked");
};

// Knowledge Base Events
export const mpKnowledgeDocumentViewed = (props: {
  documentId: string;
  documentTitle: string;
  source: string;
  status: string;
  category: string;
}) => {
  track("knowledge_document_viewed", {
    document_id: props.documentId,
    document_title: props.documentTitle,
    source: props.source,
    status: props.status,
    category: props.category,
  });
};

export const mpKnowledgeDocumentEdited = (props: {
  documentId: string;
  documentTitle: string;
}) => {
  track("knowledge_document_edited", {
    document_id: props.documentId,
    document_title: props.documentTitle,
  });
};

export const mpKnowledgeDocumentSaved = (props: {
  documentId: string;
  documentTitle: string;
}) => {
  track("knowledge_document_saved", {
    document_id: props.documentId,
    document_title: props.documentTitle,
  });
};

export const mpKnowledgeDocumentVerified = (props: {
  documentId: string;
  documentTitle: string;
  action: "verified" | "unverified";
}) => {
  track("knowledge_document_verified", {
    document_id: props.documentId,
    document_title: props.documentTitle,
    action: props.action,
  });
};

export const mpKnowledgeStatusFilterChanged = (status: string) => {
  track("knowledge_status_filter_changed", { status });
};

export const mpKnowledgeDocumentClicked = (props: {
  documentId: string;
  source: string;
}) => {
  track("knowledge_document_clicked", {
    document_id: props.documentId,
    source: props.source,
  });
};

export const mpKnowledgeSearchExecuted = (props: {
  query: string;
  resultCount: number;
  source: string;
}) => {
  track("knowledge_search_executed", {
    query: props.query,
    result_count: props.resultCount,
    source: props.source,
  });
};

export const mpKnowledgeSearchResultClicked = (props: {
  documentId: string;
  documentTitle: string;
  score: number;
  resultPosition: number;
}) => {
  track("knowledge_search_result_clicked", {
    document_id: props.documentId,
    document_title: props.documentTitle,
    score: props.score,
    result_position: props.resultPosition,
  });
};

export const mpRecommendationViewed = (props: {
  rec_type: string | null;
  entry_point: string;
  variant: "card" | "table_row" | "inline";
}) => {
  track("recommendation_viewed", props);
};

export const mpRecommendationActionClicked = (props: {
  rec_type: string | null;
  entry_point: string;
  action_label: string;
  action_destination?: string;
}) => {
  track("recommendation_action_clicked", props);
};

export const mpRecommendationDismissed = (props: {
  rec_type: string | null;
  entry_point: string;
}) => {
  track("recommendation_dismissed", props);
};

export type AuditState = "new" | "resumed";

export const mpIdeaBuildStarted = (props: {
  auditId: string | null;
  location: AuditRecommendationLocation;
  recommendation: ReturnType<typeof recommendationProperties>;
  moduleAccessStatus: "available" | "locked";
  ideaAgeDays: number | null;
}) => {
  track("idea_build_started", {
    audit_id: props.auditId,
    ...props.recommendation,
    idea_location: props.location,
    module_access_status: props.moduleAccessStatus,
    idea_age_days: props.ideaAgeDays,
  });
};

export const mpIdeaRationaleViewed = (props: {
  auditId: string | null;
  recommendation: ReturnType<typeof recommendationProperties>;
  opened: boolean;
}) => {
  track("idea_rationale_viewed", {
    audit_id: props.auditId,
    ...props.recommendation,
    opened: props.opened,
  });
};

export const mpIdeaViewed = (props: {
  auditId: string | null;
  location: AuditRecommendationLocation;
  recommendation: ReturnType<typeof recommendationProperties>;
}) => {
  track("idea_viewed", {
    audit_id: props.auditId,
    ...props.recommendation,
    idea_location: props.location,
  });
};

/**
 * `direct_link` when the app loaded on the audit, `nav` from the side nav,
 * `in_app` from any other link that names no entry point of its own.
 */
export type AuditEntryPoint =
  | "direct_link"
  | "nav"
  | "dashboard"
  | "onboarding"
  | "in_app";

export const mpAuditOpened = (props: {
  auditId: string;
  auditState: AuditState;
  entryPoint: string;
  previousPage?: string;
}) => {
  track("audit_opened", {
    audit_id: props.auditId,
    audit_state: props.auditState,
    entry_point: props.entryPoint,
    previous_page: props.previousPage,
  });
};

export const mpAnalyticsViewChanged = (props: {
  pageType: AnalyticsPageType;
  pageName?: DashboardPageName | SitewideAnalyticsPageName;
  changeType: "audience_filter" | "section_filter" | "chart_filter";
  filterChange: string | string[];
  sectionName?: string;
  chartName?: string;
}) => {
  track("analytics_view_changed", {
    page_type: props.pageType,
    page_name: props.pageName,
    change_type: props.changeType,
    filter_change: Array.isArray(props.filterChange)
      ? props.filterChange.map(camelToSnakeCase)
      : camelToSnakeCase(props.filterChange),
    section_name: props.sectionName,
    chart_name: props.chartName,
  });
};

export const mpAiTestNameSuggestionShown = (props: {
  experienceId: string;
  experienceName: string;
  originalName: string;
  suggestedName: string;
}) => {
  track("ai_test_name_suggestion_shown", {
    experience_id: props.experienceId,
    experience_name: props.experienceName,
    original_name: props.originalName,
    suggested_name: props.suggestedName,
  });
};

export const mpAiTestNameSuggestionAccepted = (props: {
  experienceId: string;
  experienceName: string;
  originalName: string;
  suggestedName: string;
  acceptedName: string;
  wasEdited: boolean;
}) => {
  track("ai_test_name_suggestion_accepted", {
    experience_id: props.experienceId,
    experience_name: props.experienceName,
    original_name: props.originalName,
    suggested_name: props.suggestedName,
    accepted_name: props.acceptedName,
    was_edited: props.wasEdited,
  });
};

export const mpAiTestNameSuggestionDismissed = (props: {
  experienceId: string;
  experienceName: string;
  originalName: string;
  suggestedName: string;
}) => {
  track("ai_test_name_suggestion_dismissed", {
    experience_id: props.experienceId,
    experience_name: props.experienceName,
    original_name: props.originalName,
    suggested_name: props.suggestedName,
  });
};

export const mpBillingPageViewed = (billingState: BillingAccountState) => {
  trackTrialFunnel(
    "billing_page_viewed",
    {
      billing_state: billingState,
    },
    { send_immediately: true }
  );
};

type MpBillingUpsellBannerViewedProps = {
  currentModuleCount: number;
  nextDiscountTier: number;
  potentialSavingsAmount: number;
};

export const mpBillingUpsellBannerViewed = ({
  currentModuleCount,
  nextDiscountTier,
  potentialSavingsAmount,
}: MpBillingUpsellBannerViewedProps) => {
  track(
    "billing_upsell_banner_viewed",
    {
      current_module_count: currentModuleCount,
      next_discount_tier: nextDiscountTier,
      potential_savings_amount: potentialSavingsAmount,
    },
    { send_immediately: true }
  );
};

type MpBillingModuleOptionsOpenedProps = {
  billingCycle?: string;
  moduleName: string;
};

export const mpBillingModuleOptionsOpened = ({
  billingCycle,
  moduleName,
}: MpBillingModuleOptionsOpenedProps) => {
  track(
    "billing_module_options_opened",
    {
      billing_cycle: billingCycle,
      module_name: moduleName,
    },
    { send_immediately: true }
  );
};

type MpBillingModuleAddStartedProps = {
  currentModuleCount: number;
  moduleName: string;
  source: "upsell_banner" | "module_card";
  tierSelected: "T1" | "T2" | "n/a";
};

export const mpBillingModuleAddStarted = ({
  currentModuleCount,
  moduleName,
  source,
  tierSelected,
}: MpBillingModuleAddStartedProps) => {
  track(
    "billing_module_add_started",
    {
      current_module_count: currentModuleCount,
      module_name: moduleName,
      source,
      tier_selected: tierSelected,
    },
    { send_immediately: true }
  );
};

type MpBillingPlanApprovalProps = {
  billingCycle: BillingCadence;
  planName: string;
};

export const mpBillingPlanApprovalModalViewed = ({
  billingCycle,
  planName,
}: MpBillingPlanApprovalProps) => {
  track(
    "billing_plan_approval_modal_viewed",
    {
      billing_cycle: billingCycle,
      plan_name: planName,
    },
    { send_immediately: true }
  );
};

export const mpBillingPlanSelected = ({
  billingCycle,
  planName,
}: MpBillingPlanApprovalProps) => {
  track(
    "billing_plan_selected",
    {
      billing_cycle: billingCycle,
      plan_name: planName,
    },
    { send_immediately: true }
  );
};

type MpBillingModuleRemoveStartedProps = {
  billingCycle?: string;
  isInCommitmentPeriod: boolean;
  moduleName: string;
};

export const mpBillingModuleRemoveStarted = ({
  billingCycle,
  isInCommitmentPeriod,
  moduleName,
}: MpBillingModuleRemoveStartedProps) => {
  track(
    "billing_module_remove_started",
    {
      billing_cycle: billingCycle,
      is_in_commitment_period: isInCommitmentPeriod,
      module_name: moduleName,
    },
    { send_immediately: true }
  );
};

type MpBillingModuleRemovedProps = {
  moduleName: string;
  remainingModules: string[];
  remainingModulesCount: number;
};

export const mpBillingModuleRemoved = ({
  moduleName,
  remainingModules,
  remainingModulesCount,
}: MpBillingModuleRemovedProps) => {
  track(
    "billing_module_removed",
    {
      module_name: moduleName,
      remaining_modules: remainingModules,
      remaining_modules_count: remainingModulesCount,
    },
    { send_immediately: true }
  );
};

type MpBillingPlanChangeCompletedProps = {
  billingCycle?: string;
  changeType: SelfServeCommandsChangeType;
  modules: string[];
  modulesCount: number;
  planName: string;
  totalAmount: number;
};

export const mpBillingPlanChangeCompleted = ({
  billingCycle,
  changeType,
  modules,
  modulesCount,
  planName,
  totalAmount,
}: MpBillingPlanChangeCompletedProps) => {
  trackTrialFunnel(
    "billing_plan_change_completed",
    {
      billing_cycle: billingCycle,
      change_type: changeType,
      modules,
      modules_count: modulesCount,
      plan_name: planName,
      total_amount: totalAmount,
    },
    { send_immediately: true }
  );
};

type MpBillingShopifyApprovalReturnedProps =
  | { approvalKind: "legacy" }
  | {
      approvalKind: "initial_subscription" | "cap_raise";
      alreadyApplied: boolean;
    };

export const mpBillingShopifyApprovalReturned = (
  props: MpBillingShopifyApprovalReturnedProps
) => {
  trackTrialFunnel(
    "billing_shopify_approval_returned",
    {
      approval_kind: props.approvalKind,
      ...(props.approvalKind === "legacy"
        ? {}
        : { already_applied: props.alreadyApplied }),
    },
    { send_immediately: true }
  );
};

export type MpBillingShopifyApprovalStartedProps = {
  approvalKind?: "initial_subscription" | "cap_raise";
  billingCycle?: BillingCadence;
  changeType?: SelfServeCommandsChangeType;
  modules?: readonly string[];
  planName?: string;
  source:
    | "plan_change"
    | "pending_approval_retry"
    | "pending_approval_resume"
    | "legacy_nudge"
    | "legacy_plan_dialog"
    | "legacy_missing_billing_button";
};

// The caller redirects out of the app in the same tick, so the beacon
// transport is what keeps the event from dying with the page.
export const mpBillingShopifyApprovalStarted = ({
  approvalKind,
  billingCycle,
  changeType,
  modules,
  planName,
  source,
}: MpBillingShopifyApprovalStartedProps) => {
  trackTrialFunnel(
    "billing_shopify_approval_started",
    {
      approval_kind: approvalKind,
      billing_cycle: billingCycle,
      change_type: changeType,
      plan_name: planName,
      source,
      ...(modules ? { modules, modules_count: modules.length } : {}),
    },
    { send_immediately: true, transport: "sendBeacon" }
  );
};

type MpBillingPlanPauseStartedProps = {
  currentPlan: string;
  activeModules: string[];
};

export const mpBillingPlanPauseStarted = ({
  currentPlan,
  activeModules,
}: MpBillingPlanPauseStartedProps) => {
  track(
    "billing_plan_pause_started",
    {
      current_plan: currentPlan,
      active_modules: activeModules,
      active_modules_count: activeModules.length,
    },
    { send_immediately: true }
  );
};

type MpBillingPlanPauseConfirmedProps = {
  currentPlan: string;
  pauseDuration?: string;
};

export const mpBillingPlanPauseConfirmed = ({
  currentPlan,
  pauseDuration,
}: MpBillingPlanPauseConfirmedProps) => {
  track(
    "billing_plan_pause_confirmed",
    {
      current_plan: currentPlan,
      pause_duration: pauseDuration,
    },
    { send_immediately: true }
  );
};

type MpBillingPlanCancelStartedProps = {
  activeModules: string[];
  currentPlan: string;
  monthsActive: number;
};

export const mpBillingPlanCancelStarted = ({
  activeModules,
  currentPlan,
  monthsActive,
}: MpBillingPlanCancelStartedProps) => {
  track(
    "billing_plan_cancel_started",
    {
      current_plan: currentPlan,
      active_modules: activeModules,
      active_modules_count: activeModules.length,
      months_active: monthsActive,
    },
    { send_immediately: true }
  );
};

type MpBillingPlanCancelConfirmedProps = {
  activeModules: string[];
  cancellationReason?: string;
  currentPlan: string;
};

export const mpBillingPlanCancelConfirmed = ({
  activeModules,
  cancellationReason,
  currentPlan,
}: MpBillingPlanCancelConfirmedProps) => {
  track(
    "billing_plan_cancel_confirmed",
    {
      active_modules: activeModules,
      active_modules_count: activeModules.length,
      cancellation_reason: cancellationReason,
      current_plan: currentPlan,
    },
    { send_immediately: true }
  );
};

type MpBillingPlanDowngradeStartedProps = {
  activeModules: string[];
  currentPlan?: string;
  targetPlan?: string;
};

export const mpBillingPlanDowngradeStarted = ({
  activeModules,
  currentPlan,
  targetPlan,
}: MpBillingPlanDowngradeStartedProps) => {
  track(
    "billing_plan_downgrade_started",
    {
      active_modules: activeModules,
      active_modules_count: activeModules.length,
      current_plan: currentPlan,
      target_plan: targetPlan,
    },
    { send_immediately: true }
  );
};

type MpBillingCheckoutErrorShownProps = {
  errorType: string;
  errorMessage: string;
  step: BillingCheckoutStep;
};

export const mpBillingCheckoutErrorShown = ({
  errorType,
  errorMessage,
  step,
}: MpBillingCheckoutErrorShownProps) => {
  trackTrialFunnel(
    "billing_checkout_error_shown",
    {
      error_type: errorType,
      error_message: errorMessage,
      step,
    },
    { send_immediately: true }
  );
};

type MpBillingTrialStartedProps = {
  isSmartPricingExcluded: boolean;
  modulesIncluded: string[];
  source: "onboarding_flow" | "billing_page" | "banner";
  trialDurationDays?: number;
};

export const mpBillingTrialStarted = ({
  isSmartPricingExcluded,
  modulesIncluded,
  source,
  trialDurationDays = 14,
}: MpBillingTrialStartedProps) => {
  trackTrialFunnel(
    "trial_started",
    {
      is_smart_pricing_excluded: isSmartPricingExcluded,
      modules_included: modulesIncluded,
      source,
      trial_duration_days: trialDurationDays,
    },
    { send_immediately: true }
  );
};

type MpBillingTrialOverviewViewedProps = {
  modulesIncluded: string[];
  trialEndDate: string;
};

export const mpBillingTrialOverviewViewed = ({
  modulesIncluded,
  trialEndDate,
}: MpBillingTrialOverviewViewedProps) => {
  trackTrialFunnel(
    "trial_overview_viewed",
    {
      modules_included: modulesIncluded,
      trial_end_date: trialEndDate,
    },
    { send_immediately: true }
  );
};

type MpBillingTrialReminderViewedProps = {
  daysRemaining: number;
  modulesActive: string[];
  trialEndDate: string;
};

export const mpBillingTrialReminderViewed = ({
  daysRemaining,
  modulesActive,
  trialEndDate,
}: MpBillingTrialReminderViewedProps) => {
  trackTrialFunnel(
    "trial_reminder_viewed",
    {
      days_remaining: daysRemaining,
      modules_active: modulesActive,
      modules_active_count: modulesActive.length,
      trial_end_date: trialEndDate,
    },
    { send_immediately: true }
  );
};

type MpModuleTrialEndingReminderViewedProps = {
  moduleId: string;
  daysRemaining: number;
  trialEndDate: string;
};

export const mpModuleTrialEndingReminderViewed = ({
  moduleId,
  daysRemaining,
  trialEndDate,
}: MpModuleTrialEndingReminderViewedProps) => {
  trackTrialFunnel(
    "module_trial_ending_reminder_viewed",
    {
      module_id: moduleId,
      days_remaining: daysRemaining,
      trial_end_date: trialEndDate,
    },
    { send_immediately: true }
  );
};

export type ModuleTrialEndingResolution =
  | "add_to_plan"
  | "view_all_plans"
  | "dismissed";

type MpModuleTrialEndingReminderResolvedProps = {
  moduleId: string;
  resolution: ModuleTrialEndingResolution;
};

export const mpModuleTrialEndingReminderResolved = ({
  moduleId,
  resolution,
}: MpModuleTrialEndingReminderResolvedProps) => {
  trackTrialFunnel(
    "module_trial_ending_reminder_resolved",
    {
      module_id: moduleId,
      resolution,
    },
    { send_immediately: true }
  );
};

type MpBillingTrialFirstTestSelectedProps = {
  moduleName: string;
  testType: string;
};

export const mpBillingTrialFirstTestSelected = ({
  moduleName,
  testType,
}: MpBillingTrialFirstTestSelectedProps) => {
  trackTrialFunnel(
    "trial_first_test_selected",
    {
      module_name: moduleName,
      test_type: testType,
    },
    { send_immediately: true }
  );
};

type MpBillingTrialSkippedProps = {
  source: "onboarding_flow" | "billing_page";
};

export const mpBillingTrialSkipped = ({
  source,
}: MpBillingTrialSkippedProps) => {
  trackTrialFunnel("trial_skipped", { source }, { send_immediately: true });
};

type MpBillingTrialConvertedProps = {
  billingCycle?: string;
  conversionTrigger: "smart_pricing_add" | "plan_approval" | "manual";
  daysIntoTrial: number;
  modulesSubscribed: string[];
  totalAmount: number;
};

export const mpBillingTrialConverted = ({
  billingCycle,
  conversionTrigger,
  daysIntoTrial,
  modulesSubscribed,
  totalAmount,
}: MpBillingTrialConvertedProps) => {
  trackTrialFunnel(
    "trial_converted",
    {
      billing_cycle: billingCycle,
      conversion_trigger: conversionTrigger,
      days_into_trial: daysIntoTrial,
      modules_subscribed: modulesSubscribed,
      modules_subscribed_count: modulesSubscribed.length,
      total_amount: totalAmount,
    },
    { send_immediately: true }
  );
};

type MpBillingTrialEndViewedProps = {
  daysRemaining: number;
  modulesActive: string[];
  trialState: "expired" | "expiring_soon";
};

export const mpBillingTrialEndViewed = ({
  daysRemaining,
  modulesActive,
  trialState,
}: MpBillingTrialEndViewedProps) => {
  trackTrialFunnel(
    "trial_end_viewed",
    {
      days_remaining: daysRemaining,
      modules_active: modulesActive,
      trial_state: trialState,
    },
    { send_immediately: true }
  );
};

/** @deprecated Use `AccountUserState` from `@src/constants/billing`. */
export type GatedModuleUserState = AccountUserState;

type MpGatedModuleModalViewedProps = {
  moduleName: string;
  userState: GatedModuleUserState;
};

export const mpGatedModuleModalViewed = ({
  moduleName,
  userState,
}: MpGatedModuleModalViewedProps) => {
  track(
    "gated_module_modal_viewed",
    {
      module_name: moduleName,
      user_state: userState,
    },
    { send_immediately: true }
  );
};

type MpGatedModuleLearnMoreViewedProps = {
  moduleName: string;
  source: "modal" | "learn_more_view";
  userState: string;
};

export const mpGatedModuleLearnMoreViewed = ({
  moduleName,
  source,
  userState,
}: MpGatedModuleLearnMoreViewedProps) => {
  track(
    "gated_module_learn_more_clicked",
    {
      module_name: moduleName,
      source,
      user_state: userState,
    },
    { send_immediately: true }
  );
};

type MpGatedModuleVideoStartedProps = {
  moduleName: string;
  source: "modal" | "learn_more_view";
  userState: string;
};

export const mpGatedModuleVideoStarted = ({
  moduleName,
  source,
  userState,
}: MpGatedModuleVideoStartedProps) => {
  track(
    "gated_module_video_started",
    {
      module_name: moduleName,
      source,
      user_state: userState,
    },
    { send_immediately: true }
  );
};

type MpGatedModuleAddStartedProps = {
  moduleName: string;
  source: "modal" | "learn_more_view";
  userState: string;
};

export const mpGatedModuleAddStarted = ({
  moduleName,
  source,
  userState,
}: MpGatedModuleAddStartedProps) => {
  track(
    "gated_module_add_started",
    {
      module_name: moduleName,
      source,
      user_state: userState,
    },
    { send_immediately: true }
  );
};

type MpGatedModuleLearnMoreDismissedProps = {
  moduleName: string;
};

export const mpGatedLearnMoreDismissed = ({
  moduleName,
}: MpGatedModuleLearnMoreDismissedProps) => {
  track(
    "gated_module_learn_more_dismissed",
    {
      module_name: moduleName,
    },
    { send_immediately: true }
  );
};

/** @deprecated Use `OrderVolumeTier` from `@src/constants/billing`. */
export type GatedModuleOrderVolumeTier = OrderVolumeTier;

type MpSmartPricingGateViewedProps = {
  ctasShown: string[];
  orderVolumeTier: GatedModuleOrderVolumeTier;
  userState: string;
};

export const mpSmartPricingGateViewed = ({
  ctasShown,
  orderVolumeTier,
  userState,
}: MpSmartPricingGateViewedProps) => {
  track(
    "smart_pricing_gate_viewed",
    {
      ctas_shown: ctasShown,
      order_volume_tier: orderVolumeTier,
      user_state: userState,
    },
    { send_immediately: true }
  );
};

type MpSmartPricingSalesContactStartedProps = {
  ctaLabel: string; //speak_to_sales / book_consultation
  orderVolumeTier: GatedModuleOrderVolumeTier;
  userState: string;
};

export const mpSmartPricingSalesContactStarted = ({
  ctaLabel,
  orderVolumeTier,
  userState,
}: MpSmartPricingSalesContactStartedProps) => {
  track(
    "smart_pricing_sales_contact_started",
    {
      cta_label: ctaLabel,
      order_volume_tier: orderVolumeTier,
      user_state: userState,
    },
    { send_immediately: true }
  );
};

type MpSmartPricingVideoStartedProps = {
  orderVolumeTier: GatedModuleOrderVolumeTier;
  userState: string;
};

export const mpSmartPricingVideoStarted = ({
  orderVolumeTier,
  userState,
}: MpSmartPricingVideoStartedProps) => {
  track(
    "smart_pricing_video_started",
    {
      order_volume_tier: orderVolumeTier,
      user_state: userState,
    },
    { send_immediately: true }
  );
};

type MpSmartPricingUpgradeStartedProps = {
  orderVolumeTier: GatedModuleOrderVolumeTier;
  tierSelected: string;
  userState: string;
};

export const mpSmartPricingUpgradeStarted = ({
  orderVolumeTier,
  tierSelected,
  userState,
}: MpSmartPricingUpgradeStartedProps) => {
  track(
    "smart_pricing_upgrade_started",
    {
      order_volume_tier: orderVolumeTier,
      tier_selected: tierSelected,
      user_state: userState,
    },
    { send_immediately: true }
  );
};

type MpSmartPricingOnboardingTimelineViewedProps = {
  orderVolumeTier: GatedModuleOrderVolumeTier;
  tierPurchased: string;
};

export const mpSmartPricingOnboardingTimelineViewed = ({
  orderVolumeTier,
  tierPurchased,
}: MpSmartPricingOnboardingTimelineViewedProps) => {
  track(
    "smart_pricing_onboarding_timeline_viewed",
    {
      order_volume_tier: orderVolumeTier,
      tier_purchased: tierPurchased,
    },
    { send_immediately: true }
  );
};

type MpExperiencePayloadInput = BasePropertiesExperience & { id: string };
type MpQaReviewState = "clean" | "issues_found" | "stale" | "running" | "idle";
type MpQaLaunchReviewOutcome = "direct" | "bypassed";

const constructQaExperienceProperties = (
  experience: MpExperiencePayloadInput
) => {
  const baseProperties = constructBasePropertiesPayload(experience);

  return {
    ...baseProperties,
    experience_type:
      experience.category === "personalization" ? "experience" : "test",
    product_category_count: baseProperties.product_categories.length,
  };
};

export const mpQaFindingDetailOpened = (params: {
  experience: MpExperiencePayloadInput;
  checkId?: string;
  checkName?: string;
  severity?: string | null;
  groupId?: string | null;
  groupLabel?: string;
  selectionType: "check_result" | "passed_group" | "passed_view_all";
  source: string;
}) => {
  track("qa_finding_detail_opened", {
    ...constructQaExperienceProperties(params.experience),
    check_id: params.checkId,
    check_name: params.checkName,
    severity: params.severity,
    group_id: params.groupId,
    group_label: params.groupLabel,
    selection_type: params.selectionType,
    source: params.source,
  });
};

// Built when the QA review or checklist lets a start through, and sent with
// mpQaTestLaunched once the test actually starts.
export const constructQaTestLaunchedProperties = (params: {
  experience: MpExperiencePayloadInput;
  launchReviewOutcome: MpQaLaunchReviewOutcome;
  qaReviewState: MpQaReviewState;
  source: string;
  unresolvedErrorCount: number;
}) => ({
  ...constructQaExperienceProperties(params.experience),
  launch_review_outcome: params.launchReviewOutcome,
  qa_review_state: params.qaReviewState,
  source: params.source,
  unresolved_error_count: params.unresolvedErrorCount,
});

export type MpQaTestLaunchedProperties = ReturnType<
  typeof constructQaTestLaunchedProperties
>;

export const mpQaTestLaunched = (properties: MpQaTestLaunchedProperties) => {
  track("qa_test_launched", properties);
};

export const mpConfirmShopifyPermissionsClicked = (missingScopes: string[]) => {
  track("confirm_shopify_permissions_clicked", {
    missing_scopes: missingScopes,
    missing_scopes_count: missingScopes.length,
  });
};

type MpBookStrategySessionClickedProps = {
  source: "get_started";
  account: AccountAnalyticsProperties | null;
  destination: StrategySessionDestination;
};

export const mpBookStrategySessionClicked = ({
  source,
  account,
  destination,
}: MpBookStrategySessionClickedProps) => {
  track(
    "book_strategy_session_clicked",
    {
      destination,
      account_data: account ? "ready" : "unavailable",
      billing_regime: account?.billingRegime,
      order_volume_tier: account?.orderVolumeTier,
      orders_per_month: account?.ordersPerMonth,
      plan_name: account?.planName,
      source,
      user_state: account?.userState,
    },
    { send_immediately: true }
  );
};

// Private functions only below this line, please add any
// new exported mixpanel track functions above this line

/**
 * The self-serve trial flow the emitting build runs, so funnel cohorts split by
 * flow without renaming events. It marks the build, not the org: some tagged
 * events also fire for legacy and non-trial orgs, so pair it with the account
 * properties to isolate trialing modular orgs.
 */
const BILLING_FLOW_VERSION = "plan_commitment_v1";

function trackTrialFunnel(
  eventName: string,
  properties: Record<string, unknown>,
  options?: RequestOptions
) {
  track(
    eventName,
    { ...properties, billing_flow_version: BILLING_FLOW_VERSION },
    options
  );
}

function track(
  eventName: string,
  properties?: Record<string, unknown>,
  options?: RequestOptions
) {
  const sanitized = Object.fromEntries(
    Object.entries(properties ?? {}).filter(([, v]) => v != null && v !== "")
  );
  mixpanel.track(
    eventName,
    { is_mobile: shouldShowMobileExperience(), ...sanitized },
    options
  );
}

function collectOffers(
  experience: Pick<
    ExperienceDto | ExperienceListDto | ExperienceFe,
    | "testTypes"
    | "id"
    | "name"
    | "category"
    | "variations"
    | "productVariantCount"
  >
) {
  return experience.variations
    .map((variation) => variation?.offer)
    .filter((offer) => offer !== null && offer !== undefined);
}

function getDocumentReferrer() {
  if (typeof document === "undefined") {
    return "direct";
  }

  return document.referrer || "direct";
}

function getDeviceType(userAgent?: string): "mobile" | "desktop" {
  if (userAgent) {
    return /Mobi|Android/i.test(userAgent) ? "mobile" : "desktop";
  }

  return shouldShowMobileExperience() ? "mobile" : "desktop";
}

function mobileAnalyticsPayloadWithEvent(
  info?: ExperienceInfo
): Record<string, unknown> {
  if (!info) {
    return { source: "mobile_analytics" };
  }

  return {
    experience_id: info.id,
    modification_types: calculateModificationTypes(info.testTypes),
    source: "mobile_analytics",
  };
}
// please add new mixpanel functions above private functions
